import { supabase } from '../lib/supabase';
import type { Comment } from '../types';
import { logger } from '../lib/logger';
import type { User } from '@supabase/supabase-js';

// Fetch comments for an incident
export async function fetchComments(incidentId: string): Promise<Comment[]> {
  try {
    logger.performance.start('fetchComments');
    
    // Simple query that directly gets the author field
    const { data, error } = await supabase
      .from('incident_comments')
      .select('*')
      .eq('incident_id', incidentId)
      .order('created_at', { ascending: true });
    
    if (error) {
      logger.error(error, 'fetchComments');
      return [];
    }
    
    // Transform the data to match our Comment type
    const comments: Comment[] = data.map(item => ({
      id: item.id,
      incident_id: item.incident_id,
      user_id: item.user_id,
      author: item.author || 'Anonymous',
      content: item.content,
      likes: item.likes || 0,
      created_at: item.created_at
    }));
    
    logger.performance.end('fetchComments');
    return comments;
  } catch (error) {
    logger.error(error, 'fetchComments');
    return [];
  }
}

// Add a new comment
export async function addComment(incidentId: string, content: string, user: User): Promise<Comment | null> {
  try {
    logger.performance.start('addComment');
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // First, get the user's profile to get their username
    const { data: profileData } = await supabase
      .from('profiles')
      .select('username')
      .eq('user_id', user.id)
      .single();
    
    const username = profileData?.username || getUsernameFromEmail(user.email || user.id);
    
    // Insert comment with author field
    const { data, error } = await supabase
      .from('incident_comments')
      .insert({
        incident_id: incidentId,
        user_id: user.id,
        content,
        author: username // Set the author field directly
      })
      .select()
      .single();
    
    if (error) {
      logger.error(error, 'addComment');
      return null;
    }
    
    const comment: Comment = {
      id: data.id,
      incident_id: data.incident_id,
      user_id: data.user_id,
      author: data.author,
      content: data.content,
      likes: data.likes || 0,
      created_at: data.created_at
    };
    
    logger.performance.end('addComment');
    return comment;
  } catch (error) {
    logger.error(error, 'addComment');
    return null;
  }
}

// Helper function to extract username from email or user ID
function getUsernameFromEmail(userIdOrEmail: string): string {
  if (!userIdOrEmail) return 'Anonymous';
  
  // If it looks like an email
  if (userIdOrEmail.includes('@')) {
    const parts = userIdOrEmail.split('@');
    return parts[0] || 'Anonymous';
  }
  
  // Otherwise just use a generic name with part of the ID
  return `User ${userIdOrEmail.substring(0, 4)}`;
}

// Like a comment
export async function likeComment(commentId: string): Promise<boolean> {
  try {
    const { error } = await supabase.rpc('increment_comment_likes', { comment_id: commentId });
    
    if (error) {
      logger.error(error, 'likeComment');
      return false;
    }
    
    return true;
  } catch (error) {
    logger.error(error, 'likeComment');
    return false;
  }
}

// Delete a comment
export async function deleteComment(commentId: string): Promise<boolean> {
  try {
    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const { error } = await supabase
      .from('incident_comments')
      .delete()
      .eq('id', commentId)
      .eq('user_id', user.id);
    
    if (error) {
      logger.error(error, 'deleteComment');
      return false;
    }
    
    return true;
  } catch (error) {
    logger.error(error, 'deleteComment');
    return false;
  }
}

// Set up real-time subscription for comments
export function subscribeToComments(incidentId: string, callback: (comment: Comment) => void) {
  const subscription = supabase
    .channel(`incident-comments-${incidentId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'incident_comments',
        filter: `incident_id=eq.${incidentId}`
      },
      (payload) => {
        // No need to fetch profile data, author is already in the payload
        const comment: Comment = {
          id: payload.new.id,
          incident_id: payload.new.incident_id,
          user_id: payload.new.user_id,
          author: payload.new.author || 'Anonymous',
          content: payload.new.content,
          likes: payload.new.likes || 0,
          created_at: payload.new.created_at
        };
        
        callback(comment);
      }
    )
    .subscribe();
  
  return subscription;
}
